import { API_URL } from "../../config";
import {
  emitLeadUpdated, emitLeadStatus, emitLeadAssigned, emitLeadNote,
  emitLeadArchived, emitLeadRestored, emitLeadSameEvent, emitLeadPrimary,
  emitLeadCreated, emitLeadActivity,
} from "./leadBus";
import { verticalStatusLabel } from "./leadVertical";

/* ═══════════════════════════════════════════════════════════════════════════
   UNIFIED LEAD SYSTEM — API SERVICE & TYPES
   Single Source of Truth for Inquiry / Pipeline / Calendar / Dashboard

   Every mutation in this file is followed by a `leadBus` emit so the
   leads table, dashboard, pipeline, calendar, and drawer all refresh
   when state changes — regardless of which surface initiated the
   mutation. See `leadBus.ts` for the transport layer.
   ═══════════════════════════════════════════════════════════════════════════ */

const hdrs = () => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

/**
 * Parse a fetch response that should be JSON. If the server returned an
 * HTML error page (Apache 403, gateway timeout, Laravel debug bar, etc.)
 * `response.json()` throws a SyntaxError whose message ("unexpected
 * character at line 1 column 1") gives the user zero useful information.
 *
 * Instead: inspect the Content-Type and the leading bytes of the body,
 * surface a meaningful diagnostic, and preserve the HTTP status so
 * callers + the auth bus can branch on 401 / 403 / 5xx normally.
 *
 * Returns the parsed JSON object on success, throws a shaped error on
 * any failure. Never returns null.
 */
async function parseJsonResponse(res: Response): Promise<any> {
  const contentType = res.headers.get("content-type") ?? "";
  const text = await res.text();

  if (contentType.includes("application/json") || /^[\s﻿]*[\[{]/.test(text)) {
    try {
      return text ? JSON.parse(text) : {};
    } catch {
      throw {
        status: res.status,
        code: res.status,
        message: `Server returned malformed JSON (HTTP ${res.status}).`,
        nonJson: true,
      };
    }
  }

  // Non-JSON body. Pick a message that helps the operator (and the on-
  // call) diagnose without opening DevTools.
  let message: string;
  if (res.status === 401) {
    message = "Your session has expired. Please log in again.";
  } else if (res.status === 403) {
    message = "Forbidden. Your account does not have access (or a server filter rejected the request).";
  } else if (res.status === 404) {
    message = "Endpoint not found.";
  } else if (res.status === 419) {
    message = "Session token mismatch. Refresh and try again.";
  } else if (res.status >= 500) {
    message = `Server error (HTTP ${res.status}). Check Laravel logs.`;
  } else if (/^\s*<!doctype html|^\s*<html/i.test(text)) {
    message = `Server returned an HTML page instead of JSON (HTTP ${res.status}).`;
  } else if (text.trim() === "") {
    message = `Empty response from server (HTTP ${res.status}).`;
  } else {
    message = `Unexpected response from server (HTTP ${res.status}).`;
  }

  throw { status: res.status, code: res.status, message, nonJson: true };
}

async function apiFetch<T>(path: string, opts: RequestInit = {}): Promise<T> {
  // ── PUT/PATCH/DELETE → POST + Laravel method spoofing ──────────────
  // Production Apache + mod_security on the live server silently rejects
  // certain PUT/PATCH/DELETE payloads (e.g. assignee swaps that flag a
  // generic "param tampering" rule) and returns the default HTML 403
  // page. POST sails through the same filter and Laravel's HandleMethods
  // middleware routes it back to the original verb's controller. Every
  // backend route + validator + auth check is unchanged; only the
  // wire-level verb the firewall sees changes.
  //
  // Local dev (no mod_security) behaves identically — Laravel resolves
  // _method spoofing transparently in every environment.
  //
  // NOTE: even with the spoofing, the JSON exception handler in
  // bootstrap/app.php and the parseJsonResponse helper below remain the
  // primary fixes. Spoofing only matters when the rejection happens
  // BEFORE the request reaches PHP.
  const method = (opts.method ?? "GET").toUpperCase();
  let finalOpts: RequestInit = opts;
  if (method === "PUT" || method === "PATCH" || method === "DELETE") {
    let body: any = {};
    try {
      body = opts.body ? JSON.parse(opts.body as string) : {};
    } catch {
      // Non-JSON body (FormData / Blob) — leave the original verb in
      // place. Method spoofing only applies to JSON bodies because the
      // helper has to inject `_method` into the payload.
    }
    if (body && typeof body === "object" && !Array.isArray(body)) {
      finalOpts = {
        ...opts,
        method: "POST",
        body: JSON.stringify({ ...body, _method: method }),
      };
    }
  }

  const res = await fetch(`${API_URL}${path}`, {
    cache: "no-store",
    ...finalOpts,
    headers: { ...hdrs(), ...((finalOpts.headers as Record<string, string>) || {}) },
  });
  const json = await parseJsonResponse(res);
  if (!res.ok) throw { status: res.status, ...json };
  return json.data;
}

const qs = (f: Record<string, any>): string => {
  const p = new URLSearchParams();
  Object.entries(f).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
  });
  const s = p.toString();
  return s ? `?${s}` : "";
};

/* ── Types ──────────────────────────────────────────────────────────────── */

export type LeadStatus =
  | "new"
  | "contacted"
  | "negotiation"
  | "ready_for_quote"
  | "ready_for_booking"
  | "confirmed"
  | "lost";

export type LeadSource = "website" | "email" | "phone" | "walk_in" | "referral" | "manual";
export type AiScore = "hot" | "warm" | "cold";
export type Priority = "low" | "medium" | "high" | "urgent";

export interface LeadThread {
  id: number;
  /** Public business tracking number — DCCL<YYMMDD>-<SEQ>. Permanent, immutable, unique. */
  lead_number?: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  organization: string | null;
  event_type: string | null;
  event_date: string | null;
  guest_count: number | null;
  budget_known: boolean;
  budget_value: number | null;
  preferred_hall: string | null;
  status: LeadStatus;
  source: LeadSource;
  ai_score: AiScore | null;
  ai_summary: string | null;
  ai_extracted_data: Record<string, any> | null;
  assigned_to: number | null;
  assigned_at: string | null;
  /** Audit list of user IDs who have ever been assigned to this lead.
   *  Maintained server-side on every reassignment; never includes the
   *  currently-assigned user. Drives the read-only historical access
   *  banner — a user whose id appears here can SEE the lead but every
   *  action button is disabled and every mutation route returns 403. */
  previous_assignee_ids?: number[] | null;
  /** Server-computed permission flag — `true` when the current request
   *  user may MUTATE this lead (admin / current assignee). Use this on
   *  the frontend rather than re-deriving from previous_assignee_ids;
   *  the model accessor is the single source of truth shared with
   *  every controller guard. Drives the "Read Only – Previously
   *  Assigned" banner + disabled action buttons. */
  can_edit?: boolean;
  /** Server-computed coarse role on this lead. Mirrors LeadAccessMode
   *  below — admin / owner / previous / none — so the frontend never
   *  has to recompute the same gating logic the backend already did. */
  access_mode?: LeadAccessMode;
  priority: Priority;
  follow_up_required: boolean;
  follow_up_at: string | null;
  is_vip: boolean;
  first_response_at: string | null;
  response_delay_minutes: number | null;
  booking_id: number | null;
  quote_id: number | null;
  customer_id: number | null;
  notes: string | null;
  // Merge / archive bookkeeping. Admin lifecycle tools surface these.
  // Under the archive-only merge contract, merging a lead sets:
  //   merged_into_lead_id, merged_at, merged_by,
  //   archived_at, archive_reason='merged', resolution_status='merged'.
  // No notes/emails/tasks/activities ever move between leads.
  merged_into_lead_id?: number | null;
  /** Wall-clock time the merge ran. NULL for archives that aren't merges. */
  merged_at?: string | null;
  /** User who performed the merge. NULL for archives that aren't merges. */
  merged_by?: number | null;
  archived_at?: string | null;
  /** Free-text human-readable archive detail (e.g. "Merged into DCCL…"). */
  archived_reason?: string | null;
  /** Categorical archive bucket — drives the Archived Leads filter chips. */
  archive_reason?: "duplicate" | "merged" | "spam" | "invalid" | "test" | null;
  /** Generated DB column — true when archived_at IS NOT NULL. Read-only. */
  is_archived?: boolean;
  resolution_status?: "active" | "merged" | "duplicate" | "archived" | "cancelled" | "converted" | "lost" | "trash" | "same_event_duplicate";
  /** True when this lead has been operator-flagged as the canonical event for its same-event group. */
  is_primary_event?: boolean;
  /** Set on a child lead pointing to its primary; primary itself has NULL. */
  related_primary_lead_id?: number | null;
  created_at: string;
  updated_at: string;
  created_by_user_id?: number | null;
  // Eager-loaded
  assignee?: TeamUser | null;
  creator?: TeamUser | null;
  inquiry?: LeadInquiry | null;
  inquiries?: LeadInquiry[];
  /**
   * Linked customer account — the SHARED account identity keyed by unique
   * phone. Multiple leads with different inquiry names (lead.name) may
   * link to the same customer record. UI must render the lead's own
   * `name` / `email` / `phone` as the "Inquiry Contact" identity and only
   * use `customer.display_name` when explicitly labeling the linked
   * customer account.
   */
  customer?: {
    id: number;
    display_name: string;
    phone: string | null;
    email: string | null;
    customerFirstName?: string | null;
    customerLastName?: string | null;
    customerCompanyName?: string | null;
  } | null;
  lead_notes?: LeadNote[];
  activities?: LeadActivity[];
  email_threads?: EmailThreadLink[];
  // Eager-loaded by getThread(): only pending/in_progress follow-up tasks,
  // ordered by due_at ascending. Drives the "Upcoming Follow-ups" card.
  tasks?: Array<{
    id: number;
    lead_id: number | null;
    title: string;
    notes: string | null;
    assigned_to: number | null;
    due_at: string;
    priority: "low" | "medium" | "high" | "urgent";
    status: "pending" | "in_progress" | "completed" | "cancelled";
    completed_at: string | null;
    created_at: string;
    // Follow-up thread bookkeeping (operational CRM extension).
    last_activity_at?: string | null;
    completed_by?: number | null;
    follow_up_attempts?: number;
    assignee?: { id: number; username: string; first_name?: string; last_name?: string } | null;
    completer?: { id: number; username: string; first_name?: string; last_name?: string } | null;
    attempts?: Array<{
      id: number;
      lead_id: number;
      task_id: number | null;
      type: "call" | "email" | "meeting" | "note";
      outcome: string | null;
      follow_up_outcome: string | null;
      notes: string | null;
      contacted_at: string;
      created_by: number | null;
      creator?: { id: number; username: string; first_name?: string; last_name?: string } | null;
    }>;
    last_attempt?: {
      id: number;
      type: "call" | "email" | "meeting" | "note";
      outcome: string | null;
      follow_up_outcome: string | null;
      contacted_at: string;
    } | null;
  }>;
}

/* ── Ownership / access ─────────────────────────────────────────────────
 *
 * Single source of truth on the frontend for "what can this user do with
 * this lead?". Mirrors the backend LeadThread::userCanAct() guard exactly
 * so the UI never tempts the user with a button the server will 403.
 *
 *   admin    — Admin / Super Admin role; full read + write on every lead.
 *   owner    — Currently-assigned user; full read + write.
 *   previous — User appears in `previous_assignee_ids`; READ-ONLY
 *              historical access. Top warning banner; every action button
 *              disabled.
 *   none     — User has no access (would normally not see the lead at all,
 *              but if they do, treat as read-only to be safe).
 * ─────────────────────────────────────────────────────────────────── */
export type LeadAccessMode = "admin" | "owner" | "previous" | "none";

export function getLeadAccessMode(
  lead: Pick<LeadThread, "assigned_to" | "previous_assignee_ids" | "access_mode"> | null | undefined,
  isAdmin: boolean,
  userId: number,
): LeadAccessMode {
  // Prefer the server-computed value when present — it accounts for
  // both the lead_assignments_history table and the legacy JSON
  // column, and is the same answer every backend guard will use.
  if (lead?.access_mode) return lead.access_mode;
  if (isAdmin) return "admin";
  if (!lead) return "none";
  if (lead.assigned_to != null && Number(lead.assigned_to) === Number(userId)) return "owner";
  const prev = lead.previous_assignee_ids ?? [];
  if (prev.some((id) => Number(id) === Number(userId))) return "previous";
  return "none";
}

/** True for any access mode that allows mutations — admin or current owner. */
export function canActOnLead(mode: LeadAccessMode): boolean {
  return mode === "admin" || mode === "owner";
}

/**
 * Normalized website-form field. Backend `LeadInquiryNormalizer` produces
 * this shape for every form submission — the label is human-readable and
 * pre-picked server-side, the value is one of the primitive types below.
 * Frontend must NOT interpret keys (no `if (key === "oap_registered")`).
 */
export type LeadCustomFieldValue =
  | string
  | number
  | boolean
  | null
  | Array<string | number | boolean | null>;

export interface LeadCustomField {
  label: string;
  value: LeadCustomFieldValue;
}

export type LeadCustomFields = Record<string, LeadCustomField>;

export interface LeadInquiry {
  id: number;
  thread_id: number;
  message: string | null;
  raw_payload: Record<string, any> | null;
  /**
   * Backend-normalized form fields keyed by canonical slug, each carrying
   * its own human-readable label. Null for legacy inquiries created before
   * the generic-intake migration — renderers must handle absent/empty.
   */
  custom_fields?: LeadCustomFields | null;
  created_at: string;
}

export interface LeadNote {
  id: number;
  thread_id: number;
  user_id: number | null;
  note: string;
  created_at: string;
}

export interface LeadActivity {
  id: number;
  thread_id: number;
  user_id: number | null;
  type: string;
  description: string | null;
  meta: Record<string, any> | null;
  // Below: returned by the backend on every row but previously omitted from
  // this type. The LeadDetailPage timeline now relies on `changes` and
  // `actor_name` to render human-readable rows for non-`created` activities
  // (status_changed / assigned / updated have description=null and put the
  // change content in `changes`).
  actor_type?: "user" | "system" | "api";
  actor_name?: string | null;
  // Appended by the LeadActivity model's accessors — drives the
  // <UserAvatar /> + role badge on every Timeline card. Null for
  // system / API actors or when the user has been deleted.
  actor_profile_image?: string | null;
  actor_role?: string | null;
  version?: number | null;
  changes?: Record<string, ActivityFieldChange> | null;
  request_id?: string | null;
  created_at: string;
}

export interface ActivityFieldChange {
  from: { value: any; label: string };
  to: { value: any; label: string };
}

export interface LeadActivityV2 {
  id: number;
  thread_id: number;
  user_id: number | null;
  type: string;
  description: string | null;
  meta: Record<string, any> | null;
  actor_type: "user" | "system" | "api";
  actor_name: string;
  actor_profile_image: string | null;
  actor_role: string | null;
  version: number | null;
  changes: Record<string, ActivityFieldChange> | null;
  request_id: string | null;
  correlation_id: string | null;
  bulk_operation_id: string | null;
  checksum: string | null;
  created_at: string;
}

export interface LeadEmail {
  id: number;
  from_name: string | null;
  from_email: string | null;
  to_emails: { name: string; email: string }[];
  subject: string | null;
  body_html: string | null;
  body_preview: string | null;
  direction: "incoming" | "outgoing";
  received_at: string;
  is_read: boolean;
}

export interface EmailThreadLink {
  id: number;
  subject: string;
  customer_email: string | null;
  linked_thread_id: number | null;
  is_private: boolean;
  last_message_at: string | null;
  unread_count: number;
  emails?: LeadEmail[];
}

export interface TeamUser {
  id: number;
  username: string;
  first_name?: string | null;
  last_name?: string | null;
  display_name?: string | null;
  /** Uploaded profile image path (relative — wrap with buildImageUrl). */
  profile_image?: string | null;
  /** Role name — /leads/view/team-users only returns lead-capable roles. */
  role_name?: string | null;
}

export interface AiSuggestion {
  /** Vertical-agnostic suggestion type.
   *  - autism/tutoring verticals emit: program, next_action, info_to_confirm, follow_up
   *  - venue vertical (DCC) emits: hall, pricing, next_action
   *  Renderers must not switch on `type` for styling — treat as opaque. */
  type: "program" | "next_action" | "info_to_confirm" | "follow_up" | "hall" | "pricing";
  title: string;
  value: string;
  reason: string;
}

export interface CalendarEvent {
  id: number | string;
  type: "inquiry_received" | "event_date";
  title: string;
  start: string;
  end: string;
  status: LeadStatus;
  color: string;
  guest_count: number | null;
  event_type: string | null;
  event_date: string | null;
  calendar_date: string | null;
  inquiry_date: string | null;
  source: LeadSource | null;
  lead: LeadThread;
}

export interface DashboardStats {
  total: number;
  by_status: Record<LeadStatus, number>;
  hot_leads: number;
  warm_leads: number;
  cold_leads: number;
  vip_leads: number;
  unassigned: number;
  follow_up_required: number;
  follow_up_today: number;
  avg_response_minutes: number;
  this_week: number;
  last_week: number;
  conversion_rate: number;
}

export interface DashboardCharts {
  leads_by_day: Record<string, number>;
  funnel: { status: string; count: number }[];
  /** Venue-vertical distribution. Kept for backwards compat; autism /
   *  tutoring should render `inquiry_subjects` instead. */
  event_types: { event_type: string; count: number }[];
  /** Vertical-neutral distribution keyed by the `program` slug on the
   *  primary inquiry's custom_fields blob. Empty on venue. */
  inquiry_subjects?: { subject: string; count: number }[];
  sources: { source: string; count: number }[];
  ai_scores: { ai_score: string; count: number }[];
}

export interface PaginationMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

/* ── Constants ─────────────────────────────────────────────────────────── */

/**
 * Base status labels. The DB-side status enum key is shared across every
 * vertical (`ready_for_booking`, `confirmed`, …); the visible label is
 * overridden per vertical via `leadVertical.ts::verticalStatusLabel`.
 *
 * Do NOT import this directly to display a status — use
 * `getStatusConfig(status)` below so the autism/tutoring vertical
 * substitutes the appropriate wording (e.g. "Ready to Enroll" instead of
 * "Ready for Booking") without any per-file feature check.
 */
const BASE_STATUS_CONFIG: Record<
  LeadStatus,
  { label: string; color: string; bgColor: string }
> = {
  new: { label: "New Inquiry", color: "#3b82f6", bgColor: "#eff6ff" },
  contacted: { label: "Contacted", color: "#8b5cf6", bgColor: "#f5f3ff" },
  negotiation: { label: "Negotiation", color: "#f59e0b", bgColor: "#fffbeb" },
  ready_for_quote: { label: "Ready for Quote", color: "#14b8a6", bgColor: "#f0fdfa" },
  ready_for_booking: { label: "Ready for Booking", color: "#22c55e", bgColor: "#f0fdf4" },
  confirmed: { label: "Confirmed", color: "#16a34a", bgColor: "#dcfce7" },
  lost: { label: "Lost", color: "#ef4444", bgColor: "#fef2f2" },
};

/** Vertical-aware status config Proxy — reading `STATUS_CONFIG[status]`
 *  returns the base entry with `label` swapped for the active vertical's
 *  override when one is defined. Keeps every call site (dozens of chip
 *  labels, filter dropdowns, KPI tiles) working without a code change. */
export const STATUS_CONFIG: Record<
  LeadStatus,
  { label: string; color: string; bgColor: string }
> = new Proxy(BASE_STATUS_CONFIG, {
  get(target, prop: string) {
    const base = target[prop as LeadStatus];
    if (!base) return base;
    // leadVertical.ts only imports TYPES from this module — no runtime cycle.
    return { ...base, label: verticalStatusLabel(prop as LeadStatus, base.label) };
  },
}) as any;

export const AI_SCORE_CONFIG: Record<
  AiScore,
  { label: string; color: string; icon: string }
> = {
  hot: { label: "Hot", color: "#ef4444", icon: "🔥" },
  warm: { label: "Warm", color: "#f59e0b", icon: "☀️" },
  cold: { label: "Cold", color: "#3b82f6", icon: "❄️" },
};

export const PRIORITY_CONFIG: Record<
  Priority,
  { label: string; color: string }
> = {
  low: { label: "Low", color: "#6b7280" },
  medium: { label: "Medium", color: "#3b82f6" },
  high: { label: "High", color: "#f59e0b" },
  urgent: { label: "Urgent", color: "#ef4444" },
};

export const PIPELINE_ORDER: LeadStatus[] = [
  "new",
  "contacted",
  "negotiation",
  "ready_for_quote",
  "ready_for_booking",
  "confirmed",
  "lost",
];

/* ── API Functions ─────────────────────────────────────────────────────── */

// List & Search
export const fetchLeads = (filters: Record<string, any> = {}) =>
  apiFetch<LeadThread[]>(`/leads${qs(filters)}`);

export const fetchLeadsWithMeta = async (
  filters: Record<string, any> = {}
): Promise<{ data: LeadThread[]; meta: PaginationMeta }> => {
  const res = await fetch(`${API_URL}/leads${qs(filters)}`, {
    headers: hdrs(),
    cache: "no-store",
  });
  const json = await res.json();
  if (!res.ok) throw json;
  return { data: json.data, meta: json.meta };
};

// Single Thread Detail
export const fetchLeadThread = (id: number) =>
  apiFetch<LeadThread>(`/leads/${id}`);

/* ── Live-update head-check ────────────────────────────────────────────
 * Lightweight "anything new?" probe used by the Inquiries page poll.
 * Hitting this every 10-15s is cheap — the backend only runs 2-3
 * COUNT(*) queries with the same visibility/filter scope as /leads,
 * and the response carries just counts plus a 3-row preview for the
 * "New inquiry received" toast.
 *
 * The frontend only fires the heavy `fetchLeadsWithMeta` when
 * `has_changes` is true.
 * ──────────────────────────────────────────────────────────────────── */

export interface LeadChangePreview {
  id: number;
  name: string | null;
  source: LeadSource | null;
  created_at: string;
}

export interface LeadChangesResult {
  has_changes: boolean;
  new_count: number;
  updated_count: number;
  preview: LeadChangePreview[];
}

/**
 * Pass the same filter object you'd send to `fetchLeadsWithMeta`,
 * plus `since_id` (highest lead id on screen) and optionally
 * `since_updated_at` (most recent updated_at) so the backend can
 * detect both inserts AND updates without you having to track every
 * row's timestamp.
 */
export const checkLeadChanges = (
  filters: Record<string, any>,
  sinceId: number,
  sinceUpdatedAt: string | null,
  signal?: AbortSignal,
): Promise<LeadChangesResult> => {
  const q: Record<string, any> = { ...filters, since_id: sinceId };
  if (sinceUpdatedAt) q.since_updated_at = sinceUpdatedAt;
  return apiFetch<LeadChangesResult>(`/leads/check-changes${qs(q)}`, { signal });
};

/* ── Real-time duplicate detection ──────────────────────────────────────
 *
 * Backend checks BOTH the clients (customers) table and the lead_threads
 * table. Returns { exists: false } when neither matches, or a richer
 * record when one does. `type` distinguishes a client hit from a lead
 * hit so the UI can route "View Existing Client" to the right surface.
 *
 * `matches` is the total count across both tables — when the same phone
 * is in both the client list AND a lead, we surface "(2 records)" so
 * staff know there's more than one record to reconcile.
 */
export interface DuplicateCheckResult {
  exists: boolean;
  /** Preferred record-type field — set on every match. */
  source_type?: "client" | "lead";
  /** Legacy alias of `source_type`. Kept for existing FE consumers. */
  type?: "client" | "lead";
  /** Generic id — same value as client_id / lead_id depending on type. */
  id?: number;
  /** Populated only on client matches. Use this when you specifically
   *  need a customers-table id (e.g. linking to the customer profile). */
  client_id?: number;
  /** Populated only on lead matches. */
  lead_id?: number;
  name?: string;
  email?: string | null;
  phone?: string | null;
  status?: LeadStatus;
  /** Combined customer + lead match count. */
  matches?: number;
  /** Customers-table-only count — drives the "auto-link single client
   *  vs. show picker on ambiguity" decision in the New Lead modal. */
  client_count?: number;
  /** Surfaced ONLY for client matches so the modal can autofill the
   *  Organization field without a follow-up customer round-trip. */
  organization?: string | null;
}

/**
 * Fire a single duplicate-check request. Pass `signal` from an
 * AbortController to cancel in-flight requests when the user keeps
 * typing — every consumer of this function (useLeadDuplicateCheck)
 * relies on cancellation to avoid race conditions between two
 * overlapping queries.
 */
export const checkDuplicateLead = (
  email?: string,
  phone?: string,
  signal?: AbortSignal,
): Promise<DuplicateCheckResult> => {
  const params = new URLSearchParams();
  if (email) params.set("email", email);
  if (phone) params.set("phone", phone);
  return apiFetch<DuplicateCheckResult>(
    `/leads/check-duplicate?${params}`,
    { signal },
  ).then((d) => d ?? { exists: false });
};

// Create / merge result shared by createLead + submitInquiry
export interface CreateLeadResult {
  data: LeadThread;
  created?: boolean;
  merged?: boolean;
  existingId?: number;
  message?: string;
}

async function postLead(path: string, body: unknown): Promise<CreateLeadResult> {
  const res = await fetch(`${API_URL}${path}`, {
    method: "POST",
    cache: "no-store",
    headers: hdrs(),
    body: JSON.stringify(body),
  });
  const json = await res.json();
  if (!res.ok) throw { status: res.status, ...json };
  return {
    data:       json.data,
    created:    json.created,
    merged:     json.merged ?? false,
    existingId: json.existing_id,
    message:    json.message,
  };
}

// Create (manual)
export const createLead = async (data: Partial<LeadThread>): Promise<CreateLeadResult> => {
  const result = await postLead("/leads", data);
  if (result?.data?.id) emitLeadCreated(result.data.id, result.data);
  return result;
};

// Update
export const updateLead = async (id: number, data: Partial<LeadThread>) => {
  const lead = await apiFetch<LeadThread>(`/leads/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
  // Two-fire pattern: a specific event (assigned / status) so surfaces
  // that only care about one kind of change can be cheap, plus the
  // catch-all "updated" event so the leads table never misses a row.
  if (Object.prototype.hasOwnProperty.call(data, "assigned_to")) {
    emitLeadAssigned(id, lead, { source: "updateLead" });
  }
  if (Object.prototype.hasOwnProperty.call(data, "status")) {
    emitLeadStatus(id, lead, { status: (data as any).status });
  }
  emitLeadUpdated(id, lead);
  return lead;
};

// Update status (drag-drop)
export const updateLeadStatus = async (id: number, status: LeadStatus) => {
  const lead = await apiFetch<LeadThread>(`/leads/${id}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
  emitLeadStatus(id, lead, { status });
  emitLeadUpdated(id, lead);
  return lead;
};

/**
 * Self-assign an unassigned lead to the current user.
 * Backend rejects (409 Conflict) if someone else already claimed it
 * and (403 Forbidden) if the current user lacks lead-manage permission.
 * The caller should refresh the unassigned list on conflict.
 */
export const claimLead = async (id: number): Promise<LeadThread> => {
  const res = await fetch(`${API_URL}/leads/${id}/claim`, {
    method: "POST",
    headers: hdrs(),
  });
  const json = await res.json();
  if (!res.ok) throw { status: res.status, ...json };
  emitLeadAssigned(id, json.data, { source: "claim" });
  emitLeadUpdated(id, json.data);
  return json.data;
};

// Website inquiry
export const submitInquiry = (data: Record<string, any>): Promise<CreateLeadResult> =>
  postLead("/leads/inquiry", data);

// Convert email → lead (always creates new; existing_leads is info-only)
export interface ConvertEmailResult {
  status: "created";
  lead_id: number;
  lead: LeadThread;
  existing_leads: LeadThread[]; // previous leads for same customer — display only
}

export const convertEmailToLead = (data: {
  email_thread_id: number;
  name: string;
  [key: string]: any;
}) =>
  apiFetch<ConvertEmailResult>("/leads/convert-email", {
    method: "POST",
    body: JSON.stringify(data),
  });

// Link email to lead
export const linkEmailToLead = (
  emailThreadId: number,
  leadThreadId: number
) =>
  apiFetch<void>("/leads/link-email", {
    method: "POST",
    body: JSON.stringify({
      email_thread_id: emailThreadId,
      lead_thread_id: leadThreadId,
    }),
  });

// Unlink email
export const unlinkEmailFromLead = (emailThreadId: number) =>
  apiFetch<void>(`/leads/unlink-email/${emailThreadId}`, {
    method: "DELETE",
  });

// Email privacy
export const setEmailPrivacy = (emailThreadId: number, isPrivate: boolean) =>
  apiFetch<void>(`/leads/email-privacy/${emailThreadId}`, {
    method: "PATCH",
    body: JSON.stringify({ is_private: isPrivate }),
  });

// Notes
export const addLeadNote = async (
  threadId: number,
  note: string,
  options: {
    /** Communication template applied (Phase 2 — analytics). */
    template_id?: number | null;
    /** Visibility scope for this note. Defaults server-side to
     *  'private_staff' when omitted. */
    visibility?: "customer_facing" | "internal" | "private_staff";
  } = {},
) => {
  const body: Record<string, unknown> = { note };
  if (options.template_id != null) body.template_id = options.template_id;
  if (options.visibility)          body.visibility  = options.visibility;
  const result = await apiFetch<LeadNote>(`/leads/${threadId}/notes`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  // Note count + lead.updated_at changes — emit both so the table and
  // dashboards refresh their counts and activity feeds.
  emitLeadNote(threadId, { action: "add" });
  emitLeadUpdated(threadId);
  return result;
};

export const deleteLeadNote = async (noteId: number) => {
  await apiFetch<void>(`/leads/notes/${noteId}`, { method: "DELETE" });
  // We don't know the parent lead id from this endpoint, so emit a
  // generic refresh-all signal — every subscriber refetches.
  emitLeadNote(0, { action: "delete", noteId });
};

// Pipeline
export const fetchPipeline = () =>
  apiFetch<Record<LeadStatus, LeadThread[]>>("/leads/view/pipeline");

// Calendar
export const fetchCalendarEvents = (from: string, to: string, mode: "inquiry" | "event" = "inquiry") =>
  apiFetch<CalendarEvent[]>(`/leads/view/calendar?from=${from}&to=${to}&mode=${mode}`);

// Dashboard
export const fetchDashboardStats = () =>
  apiFetch<DashboardStats>("/leads/view/dashboard-stats");

export const fetchDashboardCharts = () =>
  apiFetch<DashboardCharts>("/leads/view/dashboard-charts");

// AI Suggestions
export const fetchAiSuggestions = (id: number) =>
  apiFetch<AiSuggestion[]>(`/leads/${id}/ai-suggestions`);

// Team
export const fetchTeamUsers = () =>
  apiFetch<TeamUser[]>("/leads/view/team-users");

// AI Classification
export interface ClassificationResult {
  status: string;
  confidence: "high" | "medium" | "low" | "none";
  score: number;
  scores: Record<string, number>;
  applied?: boolean;
  reason?: string;
}

export const classifyEmailThread = (emailThreadId: number) =>
  apiFetch<ClassificationResult>(`/leads/classify/${emailThreadId}`);

export const reclassifyAllLeads = () =>
  apiFetch<{ count: number; message: string }>("/leads/reclassify-all", {
    method: "POST",
  });

// Lead Emails — flat list of every outbound + inbound row where
// emails.lead_id = leadId. Complements `thread.email_threads` on the
// main lead payload: threads only surface emails that participated in
// a linked email_thread; direct sends (row-action Send Email) don't
// spawn a thread and are visible only here.
export interface LeadEmailRow {
  id: number;
  direction: "outgoing" | "incoming" | string;
  subject: string | null;
  from_name: string | null;
  from_email: string | null;
  to: string[];
  status: string | null;
  body_preview: string | null;
  sent_at: string | null;
  thread_id: number | null;
}
export const fetchLeadEmails = async (
  leadId: number
): Promise<{ data: LeadEmailRow[]; meta: { total: number } }> => {
  const res = await fetch(
    `${API_URL}/leads/${leadId}/emails`,
    { headers: hdrs(), cache: "no-store" }
  );
  const json = await res.json();
  if (!res.ok) throw json;
  return { data: json.data, meta: json.meta };
};

// Activity Timeline
export const fetchLeadActivities = async (
  threadId: number,
  page = 1,
  perPage = 20
): Promise<{ data: LeadActivityV2[]; meta: PaginationMeta }> => {
  const res = await fetch(
    `${API_URL}/leads/${threadId}/activities?page=${page}&per_page=${perPage}`,
    { headers: hdrs(), cache: "no-store" }
  );
  const json = await res.json();
  if (!res.ok) throw json;
  return { data: json.data, meta: json.meta };
};

export const sendEmailFromLead = (leadId: number, data: FormData | { subject: string; body: string }) => {
  // Every successful send emits `lead.activity` so LeadActivityTimeline
  // (any subscribed surface — drawer, detail page, dashboard) refetches.
  // Without this the timeline stays cached from mount and the just-sent
  // email invisibly waits for a full browser refresh to appear.
  if (data instanceof FormData) {
    // Multipart upload (with attachments) — omit Content-Type so browser sets boundary
    const token = localStorage.getItem("token") || "";
    return fetch(`${API_URL}/leads/${leadId}/send-email`, {
      method: "POST",
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      body: data,
    }).then(async (res) => {
      const json = await res.json();
      if (!res.ok) throw json;
      emitLeadActivity(leadId, { kind: "email_sent" });
      return json.data as LeadEmail;
    });
  }
  return apiFetch<LeadEmail>(`/leads/${leadId}/send-email`, {
    method: "POST",
    body: JSON.stringify(data),
  }).then((email) => {
    emitLeadActivity(leadId, { kind: "email_sent" });
    return email;
  });
};

/* ── SMS — Phase 1 send path ──────────────────────────────────────────
 * Enqueues an SMS through the configured provider (Twilio in Phase 1).
 * Returns the persisted sms_messages row id + initial status — the
 * actual provider call runs on a queue worker, so by the time the UI
 * sees this response the SMS is "in flight" but not necessarily
 * delivered yet. Phase 3 webhooks will upgrade status → delivered. */
export interface SmsSendResponse {
  sms_id: number;
  /** sent | failed | queued | disabled.
   *  Sync queue → 'sent' or 'failed' here. Async (no worker) → 'queued'.
   *  Kill-switch off → 'disabled'. */
  status: "queued" | "sent" | "failed" | "disabled";
  phone: string;
  /** Twilio (or other provider) error message when status='failed'. */
  error_message?: string | null;
  provider_message_id?: string | null;
}
export const sendSmsFromLead = (
  leadId: number,
  payload: { message: string; phone?: string; ai_generated?: boolean; template_id?: number | null },
): Promise<SmsSendResponse> =>
  apiFetch<SmsSendResponse>(`/leads/${leadId}/sms`, {
    method: "POST",
    body: JSON.stringify(payload),
  });

/* ── SMS history — one lead + cross-lead recent feed ────────────────── */

export interface SmsHistoryRow {
  id: number;
  lead_id: number | null;
  phone: string;
  message: string;
  direction: "inbound" | "outbound";
  provider: string;
  provider_message_id: string | null;
  status: "queued" | "sent" | "failed" | "delivered" | "undelivered" | "disabled" | "received";
  ai_generated: boolean;
  sent_by: number | null;
  delivered_at?: string | null;
  failed_at?: string | null;
  error_message: string | null;
  created_at: string;
  /** Eager-loaded on the dashboard endpoint only. */
  lead?: { id: number; name: string; lead_number: string | null } | null;
}

export const fetchLeadSmsList = (leadId: number, perPage = 25): Promise<SmsHistoryRow[]> =>
  apiFetch<SmsHistoryRow[]>(`/leads/${leadId}/sms?per_page=${perPage}`);

export const fetchRecentSms = (perPage = 10): Promise<SmsHistoryRow[]> =>
  apiFetch<SmsHistoryRow[]>(`/sms/recent?per_page=${perPage}`);

/** Generate an AI-drafted SMS body. Returns the draft text + char count.
 *  Does not send — the composer can edit before firing the real send. */
export interface SmsAiDraftResponse { draft: string; length: number; }
export const generateSmsAiDraft = (leadId: number): Promise<SmsAiDraftResponse> =>
  apiFetch<SmsAiDraftResponse>(`/leads/${leadId}/sms/ai-draft`, { method: "POST" });

/** Customer-message template row from the shared email_templates table.
 *  Filtered to template_type='customer_message' for the SMS composer. */
export interface CustomerMessageTemplate {
  id: number;
  name: string;
  slug: string | null;
  description: string | null;
  /** Plain SMS body. `body` mirrors `body_text` for this type — the
   *  composer uses whichever is non-empty, preferring body_text. */
  body: string;
  body_text: string | null;
  category: string | null;
  variables: string[] | null;
}
export const fetchCustomerMessageTemplates = (): Promise<CustomerMessageTemplate[]> =>
  apiFetch<CustomerMessageTemplate[]>(`/email-hub/templates?type=customer_message&visibility=customer_facing`);

/* ── Resolution lifecycle: merge / archive / restore / related ─────────── */

export const fetchRelatedLeads = async (leadId: number): Promise<LeadThread[]> => {
  const res = await fetch(`${API_URL}/leads/${leadId}/related`, { headers: hdrs() });
  const json = await res.json();
  if (!res.ok) throw json;
  return json.data ?? [];
};

/* ── Merge target picker ──────────────────────────────────────────────────
 * Searchable, ranked merge-candidate feed. Drives the autocomplete in the
 * "Merge into another lead" dialog. Backend scores each row by phone /
 * email / customer / event / recency so the strongest candidate floats to
 * the top — the UI then renders semantic badges based on `match_reasons`.
 *
 * Reason codes (kept stable so the UI badge map can switch on them):
 *   same_phone, same_email, same_customer, similar_name,
 *   same_event_type, recent
 *
 * Pass `signal` from an AbortController so the typing debounce can cancel
 * an older request when the operator keeps typing — same pattern as
 * `checkDuplicateLead`.
 * ──────────────────────────────────────────────────────────────────── */
export type MergeCandidateReason =
  | "same_phone"
  | "same_email"
  | "same_customer"
  | "similar_name"
  | "same_event_type"
  | "recent";

export interface MergeCandidate {
  id: number;
  lead_number: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  event_type: string | null;
  event_date: string | null;
  status: LeadStatus;
  customer_id: number | null;
  created_at: string | null;
  archived_at: string | null;
  merged_into_lead_id: number | null;
  assignee: {
    id: number;
    username: string | null;
    first_name: string | null;
    last_name: string | null;
  } | null;
  match_score: number;
  match_reasons: MergeCandidateReason[];
}

export const fetchMergeCandidates = async (
  leadId: number,
  search: string = "",
  signal?: AbortSignal,
): Promise<MergeCandidate[]> => {
  const params = new URLSearchParams();
  if (search) params.set("search", search);
  const res = await fetch(
    `${API_URL}/leads/${leadId}/merge-candidates${params.toString() ? `?${params}` : ""}`,
    { headers: hdrs(), signal },
  );
  const json = await res.json();
  if (!res.ok) throw json;
  return json.data ?? [];
};

export const mergeLead = async (sourceId: number, targetId: number): Promise<LeadThread> => {
  const res = await fetch(`${API_URL}/leads/${sourceId}/merge`, {
    method: "POST",
    headers: hdrs(),
    body: JSON.stringify({ target_id: targetId }),
  });
  const json = await res.json();
  if (!res.ok || !json.status) throw new Error(json.message ?? "Merge failed");
  // Merge mutates both sides — fire archived on the source AND updated
  // on the target so every list filter (active / merged / archived)
  // refreshes correctly.
  emitLeadArchived(sourceId, { reason: "merged", target_id: targetId });
  emitLeadUpdated(targetId, json.data);
  return json.data;
};

export const archiveLead = async (
  leadId: number,
  payload: { reason?: string; resolution?: "archived" | "duplicate" | "cancelled" | "lost" }
): Promise<LeadThread> => {
  const res = await fetch(`${API_URL}/leads/${leadId}/archive`, {
    method: "POST",
    headers: hdrs(),
    body: JSON.stringify(payload),
  });
  const json = await res.json();
  if (!res.ok || !json.status) throw new Error(json.message ?? "Archive failed");
  emitLeadArchived(leadId, payload);
  emitLeadUpdated(leadId, json.data);
  return json.data;
};

export const restoreLead = async (leadId: number): Promise<LeadThread> => {
  const res = await fetch(`${API_URL}/leads/${leadId}/restore`, {
    method: "POST",
    headers: hdrs(),
  });
  const json = await res.json();
  if (!res.ok || !json.status) throw new Error(json.message ?? "Restore failed");
  emitLeadRestored(leadId);
  emitLeadUpdated(leadId, json.data);
  return json.data;
};

/* ── Same-event grouping ───────────────────────────────────────────────── */

export const markLeadAsPrimary = async (leadId: number): Promise<LeadThread> => {
  const res = await fetch(`${API_URL}/leads/${leadId}/primary-event`, {
    method: "POST",
    headers: hdrs(),
  });
  const json = await res.json();
  if (!res.ok || !json.status) throw new Error(json.message ?? "Mark primary failed");
  emitLeadPrimary(leadId);
  emitLeadUpdated(leadId, json.data);
  return json.data;
};

export const flagLeadSameEvent = async (
  leadId: number,
  payload: { primary_id: number; reason?: string }
): Promise<LeadThread> => {
  const res = await fetch(`${API_URL}/leads/${leadId}/flag-same-event`, {
    method: "POST",
    headers: hdrs(),
    body: JSON.stringify(payload),
  });
  const json = await res.json();
  if (!res.ok || !json.status) throw new Error(json.message ?? "Flag same-event failed");
  emitLeadSameEvent(leadId, payload);
  emitLeadUpdated(leadId, json.data);
  // The primary lead's same-event children list also changed.
  emitLeadUpdated(payload.primary_id);
  return json.data;
};

/* ── Assignment-aware dashboard buckets + history ──────────────────────── */

/** One row in the lead_assignments_history audit table. Every reassignment
 *  appends one row — the table never overwrites, so this stream is the
 *  canonical record of who has held the lead and when. */
export interface AssignmentHistoryRow {
  id: number;
  lead_id: number;
  assigned_from: number | null;
  assigned_to: number | null;
  changed_by: number | null;
  assigned_at: string;
  removed_at: string | null;
  is_active: boolean;
  reason: string | null;
  from_user?: TeamUser | null;
  to_user?: TeamUser | null;
  changed_by_user?: TeamUser | null;
  /** Total time this assignment was active, in seconds. Computed
   *  server-side as (removed_at ?? now) − assigned_at. Renders as
   *  "9 days", "3h", or "Active" depending on is_active. */
  duration_seconds: number;
  /** Count of LeadActivity rows authored by this assigned_to user
   *  WHILE the assignment was active (between assigned_at and
   *  removed_at / now). Drives the right-most "Activities" column
   *  per spec. Computed server-side in one batched query — no N+1. */
  activity_count: number;
  created_at: string;
  updated_at: string;
}

/** Active leads currently assigned to the request user. Backed by
 *  GET /leads/view/my-active. Paginated; same shape as fetchLeadsWithMeta. */
export const fetchMyActiveLeads = async (
  filters: Record<string, any> = {}
): Promise<{ data: LeadThread[]; meta: PaginationMeta }> => {
  const res = await fetch(`${API_URL}/leads/view/my-active${qs(filters)}`, {
    headers: hdrs(),
    cache: "no-store",
  });
  const json = await res.json();
  if (!res.ok) throw json;
  return { data: json.data, meta: json.meta };
};

/** Active leads the request user PREVIOUSLY held — read-only historical
 *  access. Drives the "Previously Assigned Leads" dashboard section.
 *  Source: lead_assignments_history rows with removed_at IS NOT NULL. */
export const fetchPreviouslyAssignedLeads = async (
  filters: Record<string, any> = {}
): Promise<{ data: LeadThread[]; meta: PaginationMeta }> => {
  const res = await fetch(`${API_URL}/leads/view/previously-assigned${qs(filters)}`, {
    headers: hdrs(),
    cache: "no-store",
  });
  const json = await res.json();
  if (!res.ok) throw json;
  return { data: json.data, meta: json.meta };
};

/** Full append-only assignment history for one lead. */
export const fetchAssignmentHistory = (leadId: number) =>
  apiFetch<AssignmentHistoryRow[]>(`/leads/${leadId}/assignment-history`);

export const trashLead = async (leadId: number, reason?: string): Promise<LeadThread> => {
  const res = await fetch(`${API_URL}/leads/${leadId}/trash`, {
    method: "POST",
    headers: hdrs(),
    body: JSON.stringify({ reason }),
  });
  const json = await res.json();
  if (!res.ok || !json.status) throw new Error(json.message ?? "Trash failed");
  emitLeadArchived(leadId, { reason: reason ?? "trash", action: "trash" });
  emitLeadUpdated(leadId, json.data);
  return json.data;
};
